/**
 * Hand-gesture emotes: recognise a few social gestures from tracked hand
 * joints and name the emote to play. Pure logic (no rendering) so it can be
 * tested with synthetic joints.
 *
 *   Wave        one open hand raised, palm out, moved side to side
 *   Clap        both open hands, palms together twice
 *   Heart       thumb tips together and index tips together, index above
 *   Thumbs up   thumb up, other fingers curled, held a moment
 *   Hands up    both open hands above your head
 *
 * Each needs a deliberate hold or repetition, then a cooldown, and the pose
 * has to be released before the same gesture can fire again.
 */
import * as THREE from 'three';

export type Finger = 'index' | 'middle' | 'ring' | 'pinky';
export const FINGERS: Finger[] = ['index', 'middle', 'ring', 'pinky'];

/** The joints the detector needs, all in the same (rig-local, y-up) space. */
export interface HandJoints {
  present: boolean;
  wrist: THREE.Vector3;
  palm: THREE.Vector3;
  palmNormal: THREE.Vector3;
  thumbTip: THREE.Vector3;
  thumbBase: THREE.Vector3;
  tips: Record<Finger, THREE.Vector3>;
  proximals: Record<Finger, THREE.Vector3>;
}

export type GestureId = 'wave' | 'clap' | 'heart' | 'thumbs' | 'handsup';
/** Emote each gesture plays (names from config/emotes). */
export const GESTURE_EMOTE: Record<GestureId, { emote: string; label: string }> = {
  wave: { emote: 'wave', label: 'Wave' },
  clap: { emote: 'clap', label: 'Clap' },
  heart: { emote: 'heart', label: 'Heart' },
  thumbs: { emote: 'fistpump', label: 'Thumbs up' },
  handsup: { emote: 'cheer', label: 'Cheer' },
};

const _a = new THREE.Vector3();
const _b = new THREE.Vector3();

/** A finger is straight when its tip is well beyond its knuckle from the wrist. */
export function fingerExtended(h: HandJoints, f: Finger) {
  const knuckle = h.proximals[f].distanceTo(h.wrist);
  return h.tips[f].distanceTo(h.wrist) > knuckle * 1.55;
}
/** Thumb sticks out: tip away from the index knuckle and further from the palm than its base. */
export function thumbExtended(h: HandJoints) {
  return h.thumbTip.distanceTo(h.proximals.index) > .055 && h.thumbTip.distanceTo(h.palm) > h.thumbBase.distanceTo(h.palm) * 1.4;
}
export const openHand = (h: HandJoints) => h.present && FINGERS.every((f) => fingerExtended(h, f));
export function thumbsUp(h: HandJoints) {
  if (!h.present || !thumbExtended(h) || FINGERS.some((f) => fingerExtended(h, f))) return false;
  _a.copy(h.thumbTip).sub(h.thumbBase).normalize();
  return _a.y > .6;
}

const HOLD_MS = 450;
const COOLDOWN_MS = 2500;

export class GestureDetector {
  private held: { id: GestureId; since: number } | null = null;
  private last: { id: GestureId; at: number } | null = null;
  /** Gesture that fired and must be released before it can fire again. */
  private latched: GestureId | null = null;
  private waveTrack: { t: number; x: number }[] = [];
  private clapOpen = false;
  private claps: number[] = [];

  /** Returns a gesture the moment it's recognised (once), else null. */
  update(now: number, left: HandJoints, right: HandJoints, head: THREE.Vector3, headQuat: THREE.Quaternion): GestureId | null {
    const pose = this.pose(left, right, head);
    if (this.latched && pose !== this.latched) this.latched = null;
    const cooling = this.last && now - this.last.at < COOLDOWN_MS;

    // Held poses.
    let fired: GestureId | null = null;
    if (pose && pose !== this.latched) {
      if (this.held?.id !== pose) this.held = { id: pose, since: now };
      else if (now - this.held.since > HOLD_MS && !cooling) fired = pose;
    } else this.held = null;

    // Motions.
    if (!fired && !cooling) fired = this.clap(now, left, right) || this.wave(now, left, right, head, headQuat);
    if (fired) {
      this.last = { id: fired, at: now };
      this.latched = fired;
      this.held = null;
      this.waveTrack = [];
      this.claps = [];
    }
    return fired;
  }

  private pose(left: HandJoints, right: HandJoints, head: THREE.Vector3): GestureId | null {
    if (left.present && right.present) {
      // Heart: thumb tips together, index tips together, index tips on top.
      const thumbs = left.thumbTip.distanceTo(right.thumbTip);
      const index = left.tips.index.distanceTo(right.tips.index);
      const topY = (left.tips.index.y + right.tips.index.y) / 2, bottomY = (left.thumbTip.y + right.thumbTip.y) / 2;
      if (thumbs < .05 && index < .05 && topY > bottomY + .03 && left.thumbTip.distanceTo(left.tips.index) > .05) return 'heart';
      if (openHand(left) && openHand(right) && left.wrist.y > head.y + .05 && right.wrist.y > head.y + .05) return 'handsup';
    }
    if (thumbsUp(right) || thumbsUp(left)) return 'thumbs';
    return null;
  }

  private clap(now: number, left: HandJoints, right: HandJoints): GestureId | null {
    if (!openHand(left) || !openHand(right)) { this.clapOpen = false; return null; }
    const d = left.palm.distanceTo(right.palm);
    if (d > .16) this.clapOpen = true;
    else if (d < .07 && this.clapOpen) {
      this.clapOpen = false;
      this.claps = this.claps.filter((t) => now - t < 1600);
      this.claps.push(now);
      if (this.claps.length >= 2) return 'clap';
    }
    return null;
  }

  private wave(now: number, left: HandJoints, right: HandJoints, head: THREE.Vector3, headQuat: THREE.Quaternion): GestureId | null {
    // One raised open hand, palm facing away from you.
    const h = [right, left].find((hand) => {
      if (!openHand(hand) || hand.wrist.y < head.y - .3) return false;
      _a.copy(head).sub(hand.palm).normalize();
      return hand.palmNormal.dot(_a) < -.2;
    });
    if (!h) { this.waveTrack = []; return null; }
    _b.set(1, 0, 0).applyQuaternion(headQuat); _b.y = 0; _b.normalize();
    const x = _a.copy(h.wrist).sub(head).dot(_b);
    this.waveTrack = this.waveTrack.filter((p) => now - p.t < 1500);
    this.waveTrack.push({ t: now, x });
    // Count direction changes with at least 4 cm of travel between them.
    let turns = 0, dir = 0, anchor = this.waveTrack[0].x;
    for (const p of this.waveTrack) {
      const dx = p.x - anchor;
      if (Math.abs(dx) < .04) continue;
      const s = Math.sign(dx);
      if (dir !== 0 && s !== dir) turns++;
      dir = s; anchor = p.x;
    }
    return turns >= 2 ? 'wave' : null;
  }
}

const GESTURES_KEY = 'gathrxr.handGestures';
/** Gesture emotes are on unless the player turned them off. */
export function gesturesEnabled() {
  try { return localStorage.getItem(GESTURES_KEY) !== '0'; } catch { return true; }
}
export function setGesturesEnabled(on: boolean) {
  try { localStorage.setItem(GESTURES_KEY, on ? '1' : '0'); } catch { /* ignore */ }
}
